import { ButtonProps, Tooltip } from 'bold-ui'
import { useViewportType } from 'hooks/useViewportType'
import { ReactComponent as SvgShareScreen } from 'images/videochamada/screen-share-filled.svg'
import { useRef } from 'react'

import { SwitchButton, SwitchButtonProps } from './SwitchButton'

type AcaoVideochamadaButtonIcon = 'mic' | 'video' | 'chat' | 'config' | 'screen'

interface AcaoVideochamadaButtonProps extends ButtonProps {
  icon: AcaoVideochamadaButtonIcon
  state: 'on' | 'off'
  unavailable?: boolean
  hasNotification?: boolean
  onClick: () => void
}

interface AcaoVideochamadaTooltipProps {
  tooltipOn?: string
  tooltipOff?: string
  unavailableTooltip?: string
  dataTestId?: string
}

export function AcaoVideochamadaButton(props: AcaoVideochamadaButtonProps) {
  const { icon, state, unavailable = false, hasNotification = false, onClick, ...rest } = props

  const { unavailableTooltip, tooltipOn, tooltipOff, dataTestId, ...restButtonProps } = acaoVideochamadaButtonRecord[
    icon
  ]
  const buttonRef = useRef<HTMLButtonElement>()
  const { isMobile } = useViewportType()

  const handleButtonClick = () => {
    if (buttonRef.current && isMobile) {
      buttonRef.current.blur()
    }
    onClick()
  }

  const isOn = state === 'on' || unavailable

  return (
    <Tooltip text={unavailable ? unavailableTooltip : isOn ? tooltipOn : tooltipOff}>
      <SwitchButton
        state={isOn ? 'on' : 'off'}
        disabled={unavailable}
        hasNotification={hasNotification}
        onClick={handleButtonClick}
        innerRef={buttonRef}
        data-testid={dataTestId}
        {...restButtonProps}
        {...rest}
      />
    </Tooltip>
  )
}

const acaoVideochamadaButtonRecord: Record<
  AcaoVideochamadaButtonIcon,
  Omit<SwitchButtonProps, 'state'> & AcaoVideochamadaTooltipProps
> = {
  mic: {
    defaultState: 'on',
    iconOn: 'microphoneFilled',
    iconOff: 'microphoneOffFilled',
    unavailableTooltip: 'Nenhum dispositivo de entrada de áudio encontrado.',
    tooltipOn: 'Desativar microfone',
    tooltipOff: 'Ativar microfone',
    dataTestId: 'SwitchButton.microfone',
  },
  video: {
    defaultState: 'on',
    iconOn: 'videoOnFilled',
    iconOff: 'videoOffFilled',
    unavailableTooltip: 'Nenhum dispositivo de entrada de vídeo encontrado.',
    tooltipOn: 'Desativar câmera',
    tooltipOff: 'Ativar câmera',
    dataTestId: 'SwitchButton.camera',
  },
  chat: {
    iconOn: 'chatFilled',
    tooltipOn: 'Ocultar mensagens',
    tooltipOff: 'Exibir mensagens',
    dataTestId: 'SwitchButton.chat',
  },
  config: {
    defaultState: 'on',
    iconOn: 'gearFilled',
    tooltipOn: 'Configurações',
    dataTestId: 'SwitchButton.config',
  },
  screen: {
    iconOn: <SvgShareScreen fill='white' fontSize='1.5rem' />,
    tooltipOn: 'Encerrar compartilhamento de tela',
    tooltipOff: 'Iniciar compartilhamento de tela',
    dataTestId: 'SwitchButton.compartilharTela',
  },
}
